import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link2, Unlink, Search, EyeOff, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const BANK_TYPES = ['sales_invoice', 'purchase_invoice', 'employee_invoice', 'expense', 'reimbursement', 'wage'];
export const money = (v, cur) => `${Number(v || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur || ''}`.trim();
const day = (v) => (v ? String(v).slice(0, 10) : '—');

const CandRow = ({ c, onLink, busy, i, prefix }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded border px-3 py-2 text-sm" data-testid={`${prefix}-${i}`}>
      <div className="min-w-0">
        <div className="font-medium">{t(`bank.types.${c.type}`)} · {c.label || '—'}</div>
        <div className="text-xs text-slate-500">{c.party || '—'} · {day(c.date)} · {money(c.amount, c.currency)}
          {c.reasons?.length > 0 && <> · {c.reasons.map((r) => t(`bank.reason.${r.replace(/ /g, '_')}`, r)).join(', ')}</>}</div>
      </div>
      <div className="flex items-center gap-2">
        {c.score > 0 && <Badge variant="outline" className="text-[10px]">{c.score}%</Badge>}
        <Button size="sm" className="h-7" disabled={busy} onClick={() => onLink(c)} data-testid={`${prefix}-link-${i}`}><Link2 className="h-3.5 w-3.5 mr-1" />{t('bank.link')}</Button>
      </div>
    </div>
  );
};

/** Review one bank transaction: confirm a suggestion, search any record to link, unlink, or ignore. */
export const BankTxDialog = ({ tx, onClose, onChanged, canEdit }) => {
  const { t } = useTranslation();
  const [type, setType] = useState(tx.amount > 0 ? 'sales_invoice' : 'purchase_invoice');
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); onChanged(r.data); if (ok) toast.success(ok); }
    catch (e) { toast.error(e.response?.data?.detail || t('bank.failed')); }
    finally { setBusy(false); }
  };
  const link = (c) => run(() => axios.post(`${API}/bank/transactions/${tx.id}/link`, { type: c.type, id: c.id, label: c.label, group: c.group || null }), t('bank.linked'));
  const search = async () => {
    try { const r = await axios.get(`${API}/bank/transactions/${tx.id}/candidates`, { params: { type, q: q || undefined } }); setResults(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || t('bank.failed')); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bank-tx-dialog">
        <DialogHeader>
          <DialogTitle className={tx.amount > 0 ? 'text-emerald-700' : 'text-slate-900'}>{money(tx.amount, tx.currency)} · {day(tx.booking_date)}</DialogTitle>
          <DialogDescription className="break-words">{tx.counterparty_name || '—'} {tx.counterparty_iban && `· ${tx.counterparty_iban}`}<br />{tx.title}</DialogDescription>
        </DialogHeader>
        {(tx.matches || []).length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold uppercase text-slate-500">{t('bank.linkedRecords')}</h4>
            {tx.matches.map((m, i) => (
              <div key={`${m.type}-${m.id}`} className="flex items-center justify-between gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm" data-testid={`bank-match-${i}`}>
                <span>{t(`bank.types.${m.type}`)} · {m.label} · {m.party} · {money(m.allocated ?? m.amount, m.currency)} {m.auto && <Badge variant="outline" className="ml-1 text-[10px]">{t('bank.auto')}</Badge>}</span>
                {canEdit && <Button size="sm" variant="ghost" className="h-7 text-red-600" disabled={busy} onClick={() => run(() => axios.post(`${API}/bank/transactions/${tx.id}/unlink`, { type: m.type, id: m.id }), t('bank.unlinked'))} data-testid={`bank-unlink-${i}`}><Unlink className="h-3.5 w-3.5 mr-1" />{t('bank.unlink')}</Button>}
              </div>
            ))}
          </div>
        )}
        {canEdit && (tx.suggestions || []).length > 0 && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold uppercase text-slate-500">{t('bank.suggestions')}</h4>
            {tx.suggestions.map((c, i) => <CandRow key={`${c.type}-${c.id}-${i}`} c={c} i={i} busy={busy} onLink={link} prefix="bank-suggestion" />)}
          </div>
        )}
        {canEdit && tx.status !== 'ignored' && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold uppercase text-slate-500">{t('bank.findRecord')}</h4>
            <div className="flex flex-wrap gap-2">
              <Select value={type} onValueChange={(v) => { setType(v); setResults(null); }}>
                <SelectTrigger className="h-8 w-52 text-xs" data-testid="bank-search-type"><SelectValue /></SelectTrigger>
                <SelectContent>{BANK_TYPES.map((k) => <SelectItem key={k} value={k}>{t(`bank.types.${k}`)}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="h-8 flex-1 min-w-[10rem] text-xs" value={q} placeholder={t('bank.searchPh')} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && search()} data-testid="bank-search-input" />
              <Button size="sm" variant="outline" className="h-8" onClick={search} data-testid="bank-search-btn"><Search className="h-3.5 w-3.5" /></Button>
            </div>
            {results && (results.length ? results.map((c, i) => <CandRow key={`${c.type}-${c.id}-${i}`} c={c} i={i} busy={busy} onLink={link} prefix="bank-result" />)
              : <p className="text-xs text-slate-500" data-testid="bank-search-empty">{t('bank.noResults')}</p>)}
          </div>
        )}
        {canEdit && !(tx.matches || []).length && (
          <Button variant="outline" size="sm" className="self-start" disabled={busy}
            onClick={() => run(() => axios.post(`${API}/bank/transactions/${tx.id}/ignore`, { ignored: tx.status !== 'ignored' }), null)} data-testid="bank-ignore-toggle">
            {tx.status === 'ignored' ? <><Eye className="h-3.5 w-3.5 mr-1" />{t('bank.unignore')}</> : <><EyeOff className="h-3.5 w-3.5 mr-1" />{t('bank.ignore')}</>}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BankTxDialog;
